export type QCReportRow = {
  id: string;
  site_id: string;
  site_name: string;
  site_address: string;
  job_id: string;
  job_scheduled_for: string;
  job_block_id: string | null;
  job_block_label: string | null;
  inspector_id: string;
  inspector_name: string | null;
  cleaner_id: string | null;
  cleaner_name: string | null;
  inspected_at: string;
  score: 1 | 2 | 3 | 4 | 5;
  passed: boolean;
  notes: string | null;
  covers_extras: boolean;
  extras_count: number;
  created_at: string;
};

export type QCVisitOption = {
  job_id: string;
  site_id: string;
  scheduled_for: string;
  status: string;
  cleaner_id: string | null;
  cleaner_name: string | null;
  block_count: number;
  extras_count: number;
  block_labels: string[];
  photo_count: number;
};

export type JobPhoto = {
  id: string;
  storage_path: string;
  uploaded_at: string;
  job_block_id: string | null;
};

export type QCCreatePayload = {
  site_id: string;
  job_id: string;
  job_block_id?: string | null;
  score: 1 | 2 | 3 | 4 | 5;
  notes?: string | null;
  covers_extras?: boolean;
};

export type QCVisitBlock = {
  id: string;
  job_id: string;
  label: string;
  area: string | null;
  source_code: string | null;
  job_kind: string;
  points: number | null;
  required_photos: number;
  status: string;
  photo_count: number;
};

export type QCBatchScore = {
  job_block_id: string;
  score: 1 | 2 | 3 | 4 | 5;
  notes?: string | null;
};

export type QCBatchPayload = {
  site_id: string;
  job_id: string;
  scores: QCBatchScore[];
  covers_extras?: boolean;
};

export type QCFilters = {
  siteId?: string;
  since?: string;
  until?: string;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function extractError(payload: unknown, fallback: string): string {
  if (typeof payload === 'string' && payload.trim()) return payload;
  if (isRecord(payload)) {
    for (const key of ['message', 'error', 'details']) {
      const value = payload[key];
      if (typeof value === 'string' && value.trim()) return value;
    }
  }
  return fallback;
}

async function requestJson<T>(path: string, token: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers);
  if (init?.body) headers.set('Content-Type', 'application/json');
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const response = await fetch(path, { ...init, headers });
  const rawText = await response.text();
  const parsed = rawText ? safeParse(rawText) : null;

  if (!response.ok) {
    throw new Error(extractError(parsed, `${response.status} ${response.statusText}`));
  }

  return parsed as T;
}

function buildQuery(filters?: QCFilters): string {
  if (!filters) return '';
  const params = new URLSearchParams();
  if (filters.siteId) params.set('site_id', filters.siteId);
  if (filters.since) params.set('since', filters.since);
  if (filters.until) params.set('until', filters.until);
  const query = params.toString();
  return query ? `?${query}` : '';
}

export async function fetchQCReports(
  token: string,
  filters?: QCFilters,
): Promise<QCReportRow[]> {
  const response = await requestJson<{ reports: QCReportRow[] }>(
    `/api/ops/qc${buildQuery(filters)}`,
    token,
    { method: 'GET' },
  );
  return response.reports ?? [];
}

export async function fetchLatestVisit(
  token: string,
  siteId: string,
): Promise<QCVisitOption | null> {
  const response = await requestJson<{ visit: QCVisitOption | null }>(
    `/api/ops/qc/latest-visit?site_id=${encodeURIComponent(siteId)}`,
    token,
    { method: 'GET' },
  );
  return response.visit ?? null;
}

export async function fetchSiteVisits(
  token: string,
  siteId: string,
  sinceDays?: number,
): Promise<QCVisitOption[]> {
  const params = new URLSearchParams({ site_id: siteId });
  if (typeof sinceDays === 'number' && Number.isFinite(sinceDays)) {
    params.set('since_days', String(sinceDays));
  }
  const response = await requestJson<{ visits: QCVisitOption[] }>(
    `/api/ops/qc/site-visits?${params.toString()}`,
    token,
    { method: 'GET' },
  );
  return response.visits ?? [];
}

export async function fetchJobPhotos(token: string, jobId: string): Promise<JobPhoto[]> {
  const response = await requestJson<{ photos: JobPhoto[] }>(
    `/api/ops/qc/job-photos?job_id=${encodeURIComponent(jobId)}`,
    token,
    { method: 'GET' },
  );
  return response.photos ?? [];
}

export async function createQCReport(
  token: string,
  payload: QCCreatePayload,
): Promise<QCReportRow> {
  return requestJson<QCReportRow>('/api/ops/qc', token, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function fetchVisitBlocks(
  token: string,
  jobId: string,
): Promise<QCVisitBlock[]> {
  const response = await requestJson<{ blocks: QCVisitBlock[] }>(
    `/api/ops/qc/visit-blocks?job_id=${encodeURIComponent(jobId)}`,
    token,
    { method: 'GET' },
  );
  return response.blocks ?? [];
}

export async function createQCReportsBatch(
  token: string,
  payload: QCBatchPayload,
): Promise<QCReportRow[]> {
  const response = await requestJson<{ reports: QCReportRow[] }>('/api/ops/qc/batch', token, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return response.reports ?? [];
}

export async function deleteQCReport(token: string, id: string): Promise<void> {
  await requestJson<{ ok: true }>(`/api/ops/qc/${encodeURIComponent(id)}`, token, {
    method: 'DELETE',
  });
}

export function mediaUrl(storagePath: string): string {
  return `/api/ops/media/${encodeURIComponent(storagePath)}`;
}
